/* eslint-disable no-nested-ternary */
import { message } from 'antd';
import _, { isString } from 'lodash';
import moment from 'moment-timezone';
import pathRegexp from 'path-to-regexp';
import { parse } from 'querystring';
import { dynamic, FormattedMessage, getIntl, getLocale, history } from 'umi';

import env from '@/config/env';
import {
  isJsonDataCreatorResource,
  resolveJsonDataCreatorDisplay,
} from '@/utils/core/data-transformation/jsonDataCreatorHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { FORM_FILED_TYPES } from './constants';
import Regexp from './utilities/regexp';
import { getSessionItem, STORAGE_KEYS } from './auth';

const reg = /(((^https?:(?:\/\/)?)(?:[-;:&=\+\$,\w]+@)?[A-Za-z0-9.-]+(?::\d+)?|(?:www.|[-;:&=\+\$,\w]+@)[A-Za-z0-9.-]+)((?:\/[\+~%\/.\w-_]*)?\??(?:[-\+=&;%@.\w_]*)#?(?:[\w]*))?)$/;

export const isUrl = (path) => reg.test(path);
export const isAntDesignPro = () => {
  if (ANT_DESIGN_PRO_ONLY_DO_NOT_USE_IN_YOUR_PRODUCTION === 'site') return true;
  return window.location.hostname === 'preview.pro.ant.design';
}; // For official demo site use, to disable features not needed in real development environment

export const isAntDesignProOrDev = () => {
  const { NODE_ENV } = process.env;
  if (NODE_ENV === 'development') return true;
  return isAntDesignPro();
};

export const getPageQuery = () => parse(window.location.search.slice(1));

/**
 * props.route.routes
 * @param router [{}]
 * @param pathname string
 */

export const getAuthorityFromRouter = (router = [], pathname) => {
  const authority = router.find(
    ({ routes, path = '/' }) =>
      (path && pathRegexp(path).exec(pathname)) ||
      (routes && getAuthorityFromRouter(routes, pathname)),
  );
  if (authority) return authority;
  return undefined;
};

export const getRouteAuthority = (path, routeData) => {
  let authorities;
  routeData.forEach((route) => {
    if (pathRegexp(`${route.path}/(.*)`).test(`${path}/`)) {
      if (route.authority) authorities = route.authority;
      if (route.path === path) authorities = route.authority || authorities;
      if (route.routes) authorities = getRouteAuthority(path, route.routes) || authorities;
    }
  });
  return authorities;
};

export const isNullOrEmpty = (value) =>
  _.isNil(value) || (_.isEmpty(value) && _.isNaN(value)) || value === '';

/**
 * Helper function to transform the $expand operator
 * @param {Object} obj
 */
const toExpand = (obj) =>
  _.map(obj, (v1, k1) => {
    const sel = [];
    const exp = [];

    _.entries(v1).forEach(([k2, v2]) => {
      // eslint-disable-next-line no-unused-expressions
      _.isEqual(k2, 'expand')
        ? exp.push(`${k1}($expand=${toExpand(v2)})`)
        : sel.push(`$${k2}=${v2}`);
    });

    return [exp.join(','), sel.length && `${k1}(${sel.join(';')})`].filter(Boolean).join(',');
  }).join(',');

export const toQuery = ({
  limit,
  page,
  select,
  expand,
  search,
  filter,
  orderBy,
  year,
  month,
  searchSelect = [],
  notSelectSearch = '',
}) => {
  let newFilter = filter ? `${filter}${search ? ' and ' : ''}` : '';
  if (search) {
    const notSelect = ['id', 'isActive', ...notSelectSearch.split(',')];
    const source = searchSelect.length ? searchSelect : select ? select.split(',') : [];
    const newSelect = source.filter((o) => !notSelect.includes(o));

    if (expand && _.isObject(expand)) {
      _.forEach(expand, (expandValue, expandKey) => {
        if (notSelect.includes(expandKey)) return;

        const pushSelect = (path, value) => {
          if (!value?.select) return;
          value.select
            .split(',')
            .map((entry) => entry.trim())
            .filter(Boolean)
            .forEach((entry) => {
              if (notSelect.includes(entry)) return;
              newSelect.push(`${path}${entry}`);
            });
        };

        pushSelect(`${expandKey}/`, expandValue);

        if (expandValue?.expand && _.isObject(expandValue.expand)) {
          _.forEach(expandValue.expand, (nestedValue, nestedKey) => {
            pushSelect(`${expandKey}/${nestedKey}/`, nestedValue);
          });
        }
      });
    }

    const uniqueSelect = _.uniq(newSelect);
    newFilter += uniqueSelect.length
      ? `(${uniqueSelect
          .map((v1) => {
            return !Number.isNaN(search) && env.QUERY_SEARCH_NUMERIC_KEYS.includes(v1)
              ? `${_.chain(v1).startCase().split(' ').join('').value()} eq ${search}`
              : `contains(${v1},'${search}')`;
          })
          .join(' or ')})`
      : null;
  }

  return {
    ...(year && { year }),
    ...(month && { month }),
    ...(limit && { $top: limit }),
    ...(page && limit && { $skip: (page - 1) * limit }),
    ...(select && { $select: select }),
    ...(expand && _.isObject(expand) && { $expand: toExpand(expand) }),
    ...(newFilter && { $filter: newFilter }),
    ...(orderBy && { $orderBy: orderBy }),
  };
};

export const toPaginate = (meta) => {
  return {
    page: meta.page,
    limit: meta.limit,
    count: meta.totalCount,
    totalPages: meta.limit ? Math.ceil(meta.totalCount / meta.limit) : 0,
  };
};

export const paginateData = ({ data = [], ...meta }) => {
  const { page, limit } = meta;
  const st = (+page - 1) * limit;
  return _.slice(data, st, Math.min(st + limit, data?.length));
};

/**
 * This function formats reputation like values for display.
 * It converts large numbers into more readable forms (e.g., "1.2k" instead of "1200").
 *
 * @param {number|string} value The value to format, which can be a number or a string.
 * @returns {string} The formatted value as a string.
 */
export const reputationLikeFormat = (value) => {
  try {
    const number = +value;
    if (!number) return '0';
    if (number < 1_000) return String(number);
    if (number < 1_000_000) return `${(number / 1_000).toFixed(1)}K`;
    if (number < 1_000_000_000) return `${(number / 1_000_000).toFixed(1)}M`;
    return `${(number / 1_000_000_000).toFixed(1)}B`;
  } catch (e) {}
};

export const assignFieldValue = (config, global, item = {}) => {
  // Validate that config and config.fields exist and are valid
  if (!config || !config.fields || !Array.isArray(config.fields)) {
    return [];
  }

  // Validate that global is defined
  const globalData = global || {};

  const edit = !_.isNil(item) && !_.isEmpty(item);
  const fields = [...config.fields].map((field) => {
    if (field.type === 'select' && !field.options?.length && !field.optionsGlobal) {
      _.set(field, 'options', globalData[field.name] || []);
    }

    let value = edit
      ? _.get(
          item,
          _.has(field, 'itemDb')
            ? field.itemDb
            : _.has(field, 'nameView')
            ? field.nameView
            : field.name,
        )
      : field.value ?? field.defaultValue ?? field.initialValue;

    // Special logic for permissions field: convert ';'-separated string to array
    if (field.name === 'permissions' && edit && value && typeof value === 'string') {
      value = value.split(';').filter(Boolean);
    }

    // Enums / numbers that start at 0: API often omits the key; with showNull treat as 0
    // (see getDetails / getEnumTranslationKey; list columns use the same default for `order`).
    if (
      edit &&
      field.showNull &&
      (value === undefined || value === null) &&
      (field.type === 'number' ||
        (field.type === 'select' && field.options?.some((option) => option.value === 0)))
    ) {
      value = 0;
    }

    if (field.type === 'switch') {
      value =
        !_.isNil(item) && !_.isEmpty(item)
          ? value || false
          : _.has(field, 'defaultChecked')
          ? field.defaultChecked
          : field.checked;
      _.set(field, 'checked', value);
      _.set(field, 'defaultChecked', value);
    }

    if (edit && _.has(field, 'rulesEdit')) {
      _.set(field, 'rules', field.rulesEdit);
    }

    if (field.type === 'datepicker' && value && field.format) {
      value = moment(value, field.format);
    }

    if (field.type === 'timepicker' && value && field.format) {
      value = moment(value, field.format);
    }

    // Transform object from expand to labelInValue format for select fields
    if (
      edit &&
      field.type === 'select' &&
      field.labelInValue &&
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      !value.label &&
      value.id &&
      (value.name || value.fullName || value.displayName || value.description)
    ) {
      value = {
        label: value.name || value.fullName || value.displayName || value.description,
        value: value.id,
      };
    }

    // EntitySelector stores `[{ id, name }, ...]`. On edit, `nameView` /
    // expand usually yields a single object (or a bare GUID on the id field).
    if (edit && field.type === 'entitySelector') {
      if (Array.isArray(value)) {
        // already picker-shaped
      } else if (value && typeof value === 'object' && value.id) {
        value = [
          {
            id: value.id,
            name: value.name || value.fullName || value.displayName || value.description,
          },
        ];
      } else if (value) {
        const expandKey =
          field.nameView || (typeof field.name === 'string' ? field.name.replace(/Id$/, '') : null);
        const expand = expandKey ? item[expandKey] : null;
        value = [
          {
            id: value,
            name: expand?.name || expand?.fullName || expand?.displayName || String(value),
          },
        ];
      } else {
        value = [];
      }
    }

    return !_.isNil(value) ? { ...field, value } : field;
  });

  return _.filter(fields, (l) => !l.detail);
};

export const formatDate = (date = new Date()) => {
  return moment(date).format('L LT');
};

export const formatUTCLocal = (date, format = 'DD/MM/YYYY H:mm:ss') => {
  return moment.utc(date).local().format(format);
};

export const formatTZLocal = (date, format = 'DD/MM/YYYY hh:mm a', isMoment = false) => {
  let dt = moment.utc(date).local();
  if (getSessionItem(STORAGE_KEYS.USER_TIMEZONE)) {
    const tz = moment.utc(date).tz(getSessionItem(STORAGE_KEYS.USER_TIMEZONE));
    if (tz.isValid()) {
      dt = tz;
    }
  }
  return isMoment ? dt : dt.format(format);
};

export const getDetails = (config, global, item = {}, showID = false, idPosition = 0) => {
  const fields = config.fields.map((field) => {
    let value = _.get(item, _.has(field, 'itemDb') ? field.itemDb : field.name);

    if (_.has(field, 'nameView')) value = item[field.nameView];

    // Backend often omits numeric defaults (0) and false-y wire defaults. With showNull,
    // resolve the same way the list does for `order` (undefined → 0) and enum selects.
    if ((value === undefined || value === null) && field.showNull) {
      if (field.type === 'number') {
        value = 0;
      }
    }

    // format: true → formatDate (locale L LT). format: 'TZLocal' → formatTZLocal (timezone usuario)
    if (_.has(field, 'format') && value) {
      value = field.format === 'TZLocal' ? formatTZLocal(value) : formatDate(value);
    }
    if (
      (field.type === 'select' || field.type === 'radio-group') &&
      field.options?.length &&
      !_.has(field, 'nameView')
    ) {
      // Align with getEnumTranslationKey: backends often omit numeric enums when value is 0.
      // With showNull, treat missing as 0 so the default option label still resolves.
      const lookupValue = (value === undefined || value === null) && field.showNull ? 0 : value;
      const label = _.find(field.options, { value: lookupValue });
      value = label ? label.label : '';
    }

    if (Regexp.isUUID(value) && !showID) {
      const [k] = _.split(field.name, 'Id');
      value = item[k]?.displayName || item[k]?.fullName || item[k]?.name;
    }

    const resolvedValue =
      field.type === 'json'
        ? value
        : typeof value === 'object' && value !== null && !field.userAddList
        ? isJsonDataCreatorResource(value)
          ? resolveJsonDataCreatorDisplay(value)
          : value?.name || value?.fullName || value?.displayName || _.values(value)[0]
        : value;

    const baseRow = {
      ..._.omit(field, ['detailAsLink']),
      value: resolvedValue,
    };

    const showAsDetailLink =
      field.detailAsLink && typeof resolvedValue === 'string' && resolvedValue.trim() !== '';

    return {
      ...baseRow,
      ...(showAsDetailLink ? { type: 'link', target: '_blank' } : {}),
    };
  });

  // Add ID field at specified position if item has an id
  const idField = item.id
    ? {
        name: 'id',
        label: 'global.text.id',
        value: item.id,
        type: 'text',
        locale: false,
        copy: true,
      }
    : null;

  if (!idField) {
    return fields;
  }

  // Insert ID at specified position
  const position = Math.max(0, Math.min(idPosition, fields.length));
  const beforePosition = fields.slice(0, position);
  const afterPosition = fields.slice(position);

  return [...beforePosition, idField, ...afterPosition];
};

export const isNilOrEmpty = (val) => _.isNil(val) || val === '' || val?.length === 0;

/**
 * Empty check for form controls (especially Ace selects).
 * Handles labelInValue `{ label, value }`, choice payloads `{ items, displayText }`,
 * and arrays of blank strings — cases `isNilOrEmpty` misses on plain objects.
 */
export const isFieldValueEmpty = (val) => {
  if (_.isNil(val) || val === '') return true;
  if (_.isBoolean(val) || _.isNumber(val)) return false;

  if (_.isArray(val)) {
    return val.length === 0 || val.every((item) => isFieldValueEmpty(item));
  }

  if (_.isPlainObject(val)) {
    if ('items' in val || 'displayText' in val) {
      const itemsEmpty = !val.items || (_.isArray(val.items) && val.items.length === 0);
      const textEmpty = _.isNil(val.displayText) || val.displayText === '';
      return itemsEmpty && textEmpty;
    }

    if ('value' in val || 'key' in val || 'label' in val || 'text' in val || 'id' in val) {
      const raw = val.value ?? val.key ?? val.id;
      const label = val.label ?? val.text;
      const rawEmpty = _.isNil(raw) || raw === '';
      const labelEmpty =
        _.isNil(label) || label === '' || (typeof label === 'string' && !label.trim());
      return rawEmpty && labelEmpty;
    }

    return _.isEmpty(val);
  }

  if (typeof val?.length === 'number') return val.length === 0;
  return false;
};

export const isTrueBoolean = (val) =>
  val === 'true' || val === '1' || val === 'True' || val === 1 || val === true;

/**
 * Helper function to map the advanced filters
 * @param {Object} data
 */
/**
 * Construye una cláusula OData `eq` o, si el valor es un array con más
 * de un elemento, un grupo de `eq` unidos por `or`. Mantiene el
 * comportamiento previo (single value → `path eq value`) y habilita la
 * selección múltiple sin romper a los consumidores actuales.
 *
 * Ejemplos:
 *   buildEqOrInClause('processId', 1)        → 'processId eq 1'
 *   buildEqOrInClause('processId', [1])      → 'processId eq 1'
 *   buildEqOrInClause('processId', [1, 2])   → '(processId eq 1 or processId eq 2)'
 *   buildEqOrInClause('processId', [])       → null  (filtro inactivo)
 */
const buildEqOrInClause = (path, value) => {
  if (Array.isArray(value)) {
    const valid = value.filter((v) => !_.isNil(v) && v !== '');
    if (valid.length === 0) return null;
    if (valid.length === 1) return `${path} eq ${valid[0]}`;
    return `(${valid.map((v) => `${path} eq ${v}`).join(' or ')})`;
  }
  if (_.isNil(value) || value === '') return null;
  return `${path} eq ${value}`;
};

export const toQueryFilter = (data) => {
  return _.map(data, (value) => {
    const {
      applicationStatus,
      taskType,
      plannedDateTime,
      reminderDateTime,
      taskImportance,
      overdueDateTime,
      processGroup,
      processInstanceId,
      processId,
      dateRange,
      updatedDateRange,
      applicantUser,
      requestsApplicantUser,
      userCreatorId,
      startedMode,
      bulkRequestTaskId,
      bulkRequestTaskStatus,
      dynamicEntityVersionOperation,
      dynamicEntityFieldEnabled,
      dynamicEntityFieldType,
    } = value || {};

    const AND = [
      !isNilOrEmpty(applicationStatus) &&
        `Status eq Automy.Core.Enums.ProcessInstanceStatus'${_.capitalize(applicationStatus)}'`,

      taskImportance &&
        `Importance eq Automy.Core.Enums.Core.Tasks.TaskImportance'${_.capitalize(
          taskImportance,
        )}'`,

      taskType && `Type eq Automy.Core.Enums.Core.Tasks.TaskType'${_.capitalize(taskType)}'`,
      buildEqOrInClause('Process/ProcessGroup/Id', processInstanceId),
      processGroup && `ProcessInstance/Process/ProcessGroupId eq ${processGroup}`,
      applicantUser && `ProcessInstance/applicantUser/id eq ${applicantUser}`,
      requestsApplicantUser && `applicantUser/id eq ${requestsApplicantUser}`,
      userCreatorId && `contains(creator,'${userCreatorId}')`,
      dynamicEntityFieldEnabled && `enabled eq ${dynamicEntityFieldEnabled}`,

      dynamicEntityVersionOperation &&
        `operation eq Automy.Core.Enums.Config.DynamicEntities.DynamicEntityVersionOperation'${dynamicEntityVersionOperation}'`,

      dynamicEntityFieldType &&
        `type eq Automy.Core.Enums.FormFieldType'${dynamicEntityFieldType}'`,

      // Date range filters using helper function
      dateRange && generateDateRangeFilter(dateRange, 'created'),
      updatedDateRange && generateDateRangeFilter(updatedDateRange, 'updated'),
      overdueDateTime && generateDateRangeFilter(overdueDateTime, 'overdueDateTime'),
      plannedDateTime && generateDateRangeFilter(plannedDateTime, 'plannedDateTime'),
      reminderDateTime && generateDateRangeFilter(reminderDateTime, 'reminderDateTime'),

      startedMode &&
        `startedMode eq Automy.Core.Enums.ProcessStartMode'${_.capitalize(startedMode)}'`,

      buildEqOrInClause('processId', processId),
      bulkRequestTaskId && `bulkRequestTaskId eq ${bulkRequestTaskId}`,
      !isNilOrEmpty(bulkRequestTaskStatus) &&
        `Status eq Automy.Core.Enums.BulkRequestTaskStatus'${bulkRequestTaskStatus}'`,
    ].filter(Boolean);

    return data.length > 1 ? `(${AND.join(' and ')})` : AND.join(' and ');
  })
    .filter(Boolean)
    .join(' or ');
};

export const fieldErrorMessage = (validationErrors, fields = null) => {
  return _.map(validationErrors, (item) => {
    const fieldType = fields?.find?.((o) => o.internalName === item.field)?.type;
    const isTable =
      fieldType === CoreEnums.FormFieldType.Table ||
      fieldType === CoreEnums.FormFieldStringType.Table;

    return {
      name: item.field,
      errors: isTable ? [' '] : item.errorMessages,
    };
  });
};

// TODO: lodash provide a _.truncate function to achieve this
export const maxTextColumn = (text) => {
  const quantity = 25;
  if (text) {
    const arrayValue = text.split(' ');
    const exits = _.find(arrayValue, (v) => v.length > quantity);
    if (!_.isEmpty(exits)) {
      const index = _.indexOf(arrayValue, exits);
      return arrayValue
        .slice(0, index + 1)
        .map((v) => (v.length > quantity ? `${v.slice(0, quantity)}...` : v))
        .join(' ');
    }
  }
  return text;
};

/**
 * Helper function to set columns as filtered
 * @param cf Columns config
 * @param ob Order By string
 * @returns
 */
export function colDefFilteredValue(cf, ob) {
  return cf.map((c) => {
    const [key, order] = ob ? ob.split(' ') : [];
    return {
      ...c,
      filtered: c.key === key && !!ob,
      filteredValue: c.key === key && [key],
      sortOrder: c.key === key && order && `${order}end`,
    };
  });
}

/**
 * Helper function to handle table's columns sort event
 * @param p Pagination
 * @param f Filters
 * @param s Sorter
 */
export function handleSort(p, f, s) {
  const sort = s.order && s.order.substr(0, s.order.length - 3);
  const [model, dispatch, reducer, saga = 'setOrderBy'] = this; // additional parameters "fn.bind"
  const value = s.order && `${s.columnKey} ${sort}`;
  dispatch({
    type: `${model}/${saga}`,
    payload: reducer ? { type: reducer, value } : value,
  });
}

/**
 * Helper function to separate long strings with spaces according to a limit
 * @param word long strings
 * @param limit number
 */
export function separateString(word, limit) {
  if (word.length > limit) {
    if (!word.includes(' ')) {
      let newWord = '';
      let res = word;
      for (let i = 0; i < Math.ceil(word.length / limit); i += 1) {
        newWord += `${res.substring(0, limit)} `;
        res = res.substring(limit, res.length);
      }
      return newWord;
    }
  }
  return word;
}

/**
 * Helper function to assign switch value init
 * @param fields fields form
 */
export const setSwitchValue = (fields, defaultChecked) => {
  const formSwitchValue = {};
  [...fields].forEach((f) => {
    if (f.type === 'switch') {
      formSwitchValue[f.name] = defaultChecked ? f.defaultChecked : f.checked || f.value;
    } else {
      formSwitchValue[f.name] = f.value;
    }
  });

  return formSwitchValue;
};

export const setSelectValue = (fields) => {
  const formSelectValue = {};
  [...fields].forEach((f) => {
    if (f.type === 'select') {
      formSelectValue[f.name] = f.value;
    }
  });

  return formSelectValue;
};

/**
 * Help function to position the focus
 * @param id field id
 */
export const focusId = (id) => {
  setTimeout(() => {
    const el = document.getElementById(id);
    if (el) {
      el.focus({ preventScroll: false });
      if (el.setSelectionRange && el.type !== 'number' && el.type !== 'file') {
        el.setSelectionRange(-1, -1);
      }
    }
  }, 100);
};

export const fieldValues = (fields, item) => {
  const values = _.map(fields, (l) => {
    return {
      name: l.name,
      value: item[_.has(l, 'itemDb') ? l.itemDb : l.name],
    };
  });
  return values;
};

/**
 * Help function to rules Edit
 * @param fields fields
 * @param isEdit isEdit
 */

export const rulesEdit = (fields, isEdit) => {
  const newFields = [];
  return _.reduce(
    fields,
    (a, b) => {
      return a.then(() => {
        return new Promise((resolve) => {
          newFields.push({
            ...b,
            rules: _.map(b.rules, (it) => {
              return {
                ...it,
                required: b.hidden
                  ? false
                  : isEdit
                  ? _.has(b, 'requiredEdit')
                    ? b.requiredEdit
                    : b.requiredDefault
                  : _.has(b, 'requiredDefault')
                  ? b.requiredDefault
                  : it.required,
              };
            }),
          });
          return resolve(newFields);
        });
      });
    },
    Promise.all([]),
  );
};

/**
 * Help function To Letters Color
 * @param text text
 */
export const ToColorFromTitleCaseLetters = (fullName) => {
  try {
    if (_.isEmpty(fullName) || _.isNull(fullName)) {
      return '#0000FF';
    }

    const result = [...fullName.trim().matchAll('[A-Z]')].map((match) => match[0]).join('');
    let hash = 1;

    if (result.length > 1) {
      hash = (result[0].charCodeAt(0) - 64) * 100 + result[1].charCodeAt(0) - 64;

      // https://stackoverflow.com/questions/14204827/ms-chart-for-net-predefined-palettes-color-list
      const colors = [
        '008000',
        '0000FF',
        '800080',
        '800080',
        'FF00FF',
        '008080',
        'C1C132',
        '808080',
        '00FFFF',
        '000080',
        '800000',
        'FF3939',
        '7F7F00',
        'C0C0C0',
        'FF6347',
        'FFE4B5',
        '33023',
        'B8860B',
        'C04000',
        '6B8E23',
        'CD853F',
        'C0C000',
        '228B22',
        'D2691E',
        '808000',
        '20B2AA',
        'F4A460',
        '00C000',
        '8FBC8B',
        'B22222',
        '843A05',
        'C00000',
      ];

      const max = ('Z'.charCodeAt(0) - 64) * 100 + 'Z'.charCodeAt(0) - 64;
      const index = (hash / max) * colors.length;

      // eslint-disable-next-line radix
      return `#${colors[parseInt(index)]}`;
    }
  } catch {}

  return '#0000FF';
};

/**
 * Help function to N Letters
 * @param text text
 */
export const ToNLetters = (text) => {
  if (_.isEmpty(text) || _.isNull(text)) return '';

  const letters = 2;

  if (text.trim().length <= letters) return _.toUpper(text.trim());
  if (text.trim().split(' ').length < 2) return _.toUpper(text.trim().slice(0, letters));
  try {
    const arr = text.trim().split(' ');
    if (arr.length > 0) return _.toUpper(`${arr[0][0]}${arr[1][0]}`);
  } catch {}

  return _.toUpper(text.trim().slice(0, letters));
};

export const excelSupportedFormats = [
  '.csv',
  '.ods',
  '.xla',
  '.xls',
  '.xlsx',
  '.xps',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

/**
 * Check if string is Json like
 * @param {String} str
 * @returns Boolean
 */
export function isJsonLike(str = '') {
  if (!isString(str)) return false;
  const temp = str.trim();
  return (
    (temp.startsWith('{') && temp.endsWith('}')) || (temp.startsWith('[') && temp.endsWith(']'))
  );
}

// Utility to parse JSON safely
export const parseJson = (value) => {
  try {
    return isString(value) && isJsonLike(value) ? JSON.parse(value) : value;
  } catch {
    return value;
  }
};

/**
 * Formats a date into a relative time string (e.g., "15m ago" / "Hace 15 minutos")
 * honoring the current locale.
 * @param {string|Date|moment.Moment} value - The date to format.
 * @returns {string}
 */
export const formatRelativeTime = (value, localeParam) => {
  if (!value) return '';

  const date = moment.utc(value).local();
  const now = moment();
  const locale = localeParam || getLocale();
  const isSpanish = locale === 'es-ES';

  const diffMinutes = now.diff(date, 'minutes');
  if (diffMinutes < 60) {
    const minutes = Math.max(diffMinutes, 1);
    return isSpanish
      ? `Hace ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`
      : `${minutes}m ago`;
  }

  const diffHours = now.diff(date, 'hours');
  if (diffHours < 24) {
    return isSpanish
      ? `Hace ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`
      : `${diffHours}h ago`;
  }

  const diffDays = now.diff(date, 'days');
  if (diffDays < 7) {
    return isSpanish ? `Hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}` : `${diffDays}d ago`;
  }

  const diffWeeks = now.diff(date, 'weeks');
  if (diffWeeks < 4) {
    const weeks = Math.max(diffWeeks, 1);
    return isSpanish ? `Hace ${weeks} ${weeks === 1 ? 'semana' : 'semanas'}` : `${weeks}w ago`;
  }

  const diffMonths = now.diff(date, 'months');
  if (diffMonths < 12) {
    const months = Math.max(diffMonths, 1);
    return isSpanish ? `Hace ${months} ${months === 1 ? 'mes' : 'meses'}` : `${months}mo ago`;
  }

  const diffYears = now.diff(date, 'years') || 1;
  return isSpanish ? `Hace ${diffYears} ${diffYears === 1 ? 'año' : 'años'}` : `${diffYears}y ago`;
};

/**
 * Formats a date into the Automy absolute date-time standard
 * (e.g., "01/04/2026 23:22pm"), respecting the user's configured
 * timezone. Matches the pattern used by the details timeline.
 * @param {string|Date|moment.Moment} value - The date to format.
 * @returns {string}
 */
export const formatAbsoluteDateTime = (value) => {
  if (!value) return '';

  return formatTZLocal(value, 'L H:mma');
};

/**
 * Formats a date with full audit precision (seconds + timezone offset),
 * e.g., "01/04/2026 23:22:48 GMT-06:00". Intended for tooltips on
 * timestamps that need to be legally/audit verifiable.
 * @param {string|Date|moment.Moment} value
 * @returns {string}
 */
export const formatAuditDateTime = (value) => {
  if (!value) return '';

  return formatTZLocal(value, 'L HH:mm:ss [GMT]Z');
};

/**
 * Returns an ISO-8601 UTC string suitable for use inside
 * <time datetime="..."> elements to preserve machine readability
 * regardless of locale.
 * @param {string|Date|moment.Moment} value
 * @returns {string}
 */
export const toIsoUtc = (value) => {
  if (!value) return '';

  return moment.utc(value).toISOString();
};

/**
 * Sort arrays by order or display text
 * @param {Object} a
 * @param {Object} b
 */
export function sortFn(a, b) {
  const order = (a.order ?? 0) - (b.order ?? 0);

  if (!order) {
    if (a.displayText < b.displayText) return -1;
    if (a.displayText > b.displayText) return 1;
    return 0;
  }

  return order;
}

/**
 * Deep diff between two object-likes
 * @author https://gist.github.com/Yimiprod/7ee176597fef230d1451?permalink_comment_id=3415430#gistcomment-3415430
 * @param {Object} toObject the updated object
 * @return {Object} a new object which represents the diff
 */
function deepDiff(fromObject, toObject) {
  const changes = {};

  const buildPath = (path, obj, key) => (_.isUndefined(path) ? key : `${path}.${key}`);
  const walk = (fromObject, toObject, path) => {
    for (const key of _.keys(fromObject)) {
      const currentPath = buildPath(path, fromObject, key);
      if (!_.has(toObject, key)) changes[currentPath] = { from: _.get(fromObject, key) };
    }

    for (const [key, to] of _.entries(toObject)) {
      const currentPath = buildPath(path, toObject, key);
      if (!_.has(fromObject, key)) changes[currentPath] = { to };
      else {
        const from = _.get(fromObject, key);
        if (!_.isEqual(from, to)) {
          if (_.isObjectLike(to) && _.isObjectLike(from)) walk(from, to, currentPath);
          else changes[currentPath] = { from, to };
        }
      }
    }
  };

  walk(fromObject, toObject);
  return changes;
}

// you can use _.mixin to add the function into lodash.
_.mixin({ deepDiff });

/**
 * Format compact numbers
 * i.e. 1,234 -> 1.2k
 * @param {number} value
 */
export function numberFormat(value) {
  const formatter = Intl.NumberFormat('en', { notation: 'compact' });
  return formatter.format(value);
}

/**
 * Load component dynamically on demand.
 * https://v3.umijs.org/api
 * @param dImport: Dynamic import "import()"
 */
export const umiDynamic = (dImport) => {
  return dynamic({
    loader: async function () {
      // webpackChunkName tells webpack create separate bundle for HugeA
      const { default: Component } = await dImport;
      // import(/* webpackChunkName: "external_A" */ './localComponent');
      return Component;
    },
    loading: () => null,
  });
};

/**
 * Enhances each item in an array of form field data with type information from a provided type map.
 *
 * @param {Array} data - The array of form field data to enhance.
 * @returns {Array} The enhanced array of form field data, with 'typeName' and 'typeLabel' properties added.
 */
export const enhanceFormFieldDataWithTypes = (data) => {
  return data.map((item) => {
    let matchingType = FORM_FILED_TYPES[item.type];
    if (matchingType) {
      return {
        ...item,
        typeName: matchingType.name,
        typeLabel: matchingType.label,
      };
    }
    matchingType = FORM_FILED_TYPES[1];
    return {
      ...item,
      typeName: matchingType.name,
      typeLabel: matchingType.label,
    };
  });
};

/**
 * Asynchronously init an incremental pull process with cleanup capability
 * Supports cross-tab synchronization using localStorage and BroadcastChannel
 *
 * @param {Function} callback - The function to be called at each interval
 * @param {number} maxRetries - The total number of retries
 * @param {number[]} intervals - Array of polling interval times in seconds
 * @param {boolean} shouldLoop - Whether to loop intervals indefinitely
 * @param {string} userId - User ID for scoping
 * @param {string} itemId - Item ID for scoping
 * @returns {Object} Object with cleanup function and channel for cross-tab communication
 */
export function initIncrementalPolling(
  callback,
  maxRetries = env.MAX_POLLING_RETRIES,
  intervals = env.DEFAULT_POLLING_INTERVALS,
  shouldLoop = false,
  userId,
  itemId,
) {
  if (!userId || !itemId) {
    return { cleanup: () => {}, channel: null };
  }

  let tries = 0;
  let stopped = false;
  let timeoutId = null;

  const pollingKey = `x-incrementalPollingEnabled-${userId}-${itemId}`;
  const leaderKey = `x-pollingLeader-${userId}-${itemId}`;
  const channelName = `polling-sync-${userId}-${itemId}`;

  let channel = null;
  let isLeader = false;
  let heartbeatInterval = null;

  try {
    // Try to create BroadcastChannel
    channel = new BroadcastChannel(channelName);
  } catch (e) {
    // BroadcastChannel not supported, fallback to single-tab polling with sessionStorage
    sessionStorage.setItem(pollingKey, '1');
  }

  const count = intervals.length;
  const cleanup = () => {
    stopped = true;

    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }

    if (channel) {
      localStorage.removeItem(pollingKey);
      if (isLeader) {
        localStorage.removeItem(leaderKey);
        try {
          channel.postMessage({ type: 'leader-resigned', userId, itemId });
        } catch (e) {}
      }
      try {
        channel.close();
      } catch (e) {}
      channel = null;
    } else sessionStorage.removeItem(pollingKey);
  };

  const tryBecomeLeader = () => {
    const currentLeader = localStorage.getItem(leaderKey);
    const now = Date.now();
    // If no leader or leader heartbeat is stale (>10s), become leader
    if (!currentLeader || now - parseInt(currentLeader, 10) > 10000) {
      localStorage.setItem(leaderKey, String(now));
      localStorage.setItem(pollingKey, '1');
      isLeader = true;
      return true;
    }
    return false;
  };

  const startHeartbeat = () => {
    if (heartbeatInterval) clearInterval(heartbeatInterval);
    heartbeatInterval = setInterval(() => {
      if (isLeader && !stopped) {
        localStorage.setItem(leaderKey, String(Date.now()));
      }
    }, 3000);
  };

  const poll = async () => {
    if (!isLeader || stopped) return;

    while (!stopped && isLeader) {
      for (const interval of intervals) {
        if (stopped || !isLeader) return;

        const polling = channel
          ? localStorage.getItem(pollingKey)
          : sessionStorage.getItem(pollingKey);

        if (!polling) {
          cleanup();
          return;
        }

        // Broadcast loading-start before callback
        if (channel && isLeader) {
          try {
            channel.postMessage({
              type: 'loading-start',
              userId,
              itemId,
              timestamp: Date.now(),
            });
          } catch (e) {}
        }

        try {
          await Promise.resolve(callback());
          // Broadcast refresh (acts as loading-end)
          if (channel && isLeader) {
            try {
              channel.postMessage({
                type: 'refresh',
                userId,
                itemId,
                timestamp: Date.now(),
              });
            } catch (e) {}
          }
        } catch (e) {
          if (channel && isLeader) {
            try {
              channel.postMessage({
                type: 'loading-end',
                userId,
                itemId,
                timestamp: Date.now(),
              });
            } catch (e) {}
          }
        }

        if (tries >= maxRetries || (!shouldLoop && tries >= count) || !polling || stopped) {
          cleanup();
          return;
        }

        await new Promise((resolve) => {
          if (polling && !stopped && isLeader) {
            timeoutId = setTimeout(resolve, interval * 1000);
          } else resolve();
        });

        tries++;
      }

      if (!shouldLoop) break;
      tries = 0;
    }
  };

  if (channel) {
    channel.onmessage = (event) => {
      const { type } = event.data;

      if (type === 'leader-resigned' && !isLeader && !stopped) {
        // Attempt to become new leader after brief delay
        setTimeout(() => {
          if (!stopped && tryBecomeLeader()) {
            startHeartbeat();
            poll().catch(() => cleanup());
          }
        }, 100);
      }
    };

    if (tryBecomeLeader()) {
      startHeartbeat();
      poll().catch(() => cleanup());
    }
  } else {
    isLeader = true;
    poll().catch(() => cleanup());
  }

  return { cleanup, channel };
}

/**
 * Handle `click` and `ctrl+click` event
 * @param {Event} e Click event
 * @param {String} url URL string
 */
export const handleClick = (e, url) => {
  const ev = e?.domEvent || e;
  if (ev.ctrlKey || ev.metaKey) {
    window.open(window.location.origin + url, '_blank', 'noreferrer');
  } else history?.push(url);
};

export function createNewUUID() {
  let dt = new Date().getTime();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (dt + Math.random() * 16) % 16 | 0;
    dt = Math.floor(dt / 16);
    return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export const translate = (id, values = {}) => <FormattedMessage id={id} values={values} />;

export function hideAllOffCanvas() {
  // Close all off-canvas menus
  Array.from(document.getElementsByClassName('off-canvas')).forEach((el) => {
    const element = document.getElementById(el.id);
    if (element) {
      element.style.width = '0';
      element.style.paddingLeft = '0';
    }
  });

  // Clean all active CSS classes from main
  const main = document.querySelector('.ant-pro-basicLayout > section > section');
  if (main) {
    main.classList.remove('ant-menu-configuration-active', 'ant-menu-tasks-active');
  }
}

export function parseCurrencyToNumber(valueString) {
  try {
    if (valueString == null || valueString === '') return;
    // Step 1: Remove currency symbols ($), percentage (%), and other non-numeric chars except dots, commas, minus
    const cleaned = valueString.toString().replace(/[$%]/g, '').trim();
    if (/^0\d+$/.test(cleaned)) return valueString; // As leading zeros are a string formatting concern

    // Step 2: Split on the last decimal separator (either . or ,) to preserve decimals
    const parts = cleaned.split(/[.,](?=\d+$)/); // Split on last . or , followed by digits
    const numberPart = parts[0].replace(/[.,]/g, ''); // Remove thousand separators from integer part
    const decimalPart = parts[1] || ''; // Keep decimal part if it exists

    // Step 3: Combine and parse
    const finalString = decimalPart ? `${numberPart}.${decimalPart}` : numberPart;
    if (finalString == null || finalString === '' || isNaN(finalString)) return valueString;

    return parseFloat(finalString);
  } catch (e) {}
}

/**
 * Get filters from global filters
 * @param {Object} globalFilters
 * @returns {String}
 */
// Helper function to generate date range filters
const generateDateRangeFilter = (value, fieldName) => {
  return [
    `${fieldName} gt ${_.head(value).format(env.DATE_RANGE_START_FORMAT)}`,
    `${fieldName} lt ${_.last(value).endOf('day').format(env.DATE_RANGE_END_FORMAT)}`,
  ].join(' and ');
};

// Mapping of date filter keys to their corresponding OData field names
const dateFilterMap = {
  dateRange: 'created',
  updatedDateRange: 'updated',
  overdueDateTime: 'overdueDateTime',
  plannedDateTime: 'plannedDateTime',
  reminderDateTime: 'reminderDateTime',
};

//TODO
const resolveRegisteredFilterConfig = (FilterConfigRegistry, moduleNamespace, key) => {
  if (
    moduleNamespace &&
    FilterConfigRegistry[moduleNamespace] &&
    FilterConfigRegistry[moduleNamespace][key]
  ) {
    return FilterConfigRegistry[moduleNamespace][key];
  }
  return FilterConfigRegistry[key] || null;
};

const formatRegisteredFilterClause = (key, actualValue, filterConfig) => {
  if (filterConfig?.isEnum && filterConfig?.enumNamespace) {
    const fieldName = filterConfig?.oDataField || key;
    let enumValue;

    if (filterConfig.enumSource) {
      enumValue = Object.keys(filterConfig.enumSource).find(
        (enumKey) => filterConfig.enumSource[enumKey] === actualValue,
      );
    } else if (filterConfig.enumMap) {
      enumValue = filterConfig.enumMap[actualValue];
    }

    if (enumValue) {
      return `${fieldName} eq ${filterConfig.enumNamespace}'${enumValue}'`;
    }
  }

  let transformedValue = actualValue;
  if (filterConfig?.valueTransform) {
    transformedValue = filterConfig.valueTransform(actualValue);
  }

  const needsQuotes = filterConfig?.needsQuotes === true;
  const fieldName = filterConfig?.oDataField || key;
  const escapedValue =
    needsQuotes && typeof transformedValue === 'string'
      ? transformedValue.replace(/'/g, "''")
      : transformedValue;
  const formattedValue = needsQuotes ? `'${escapedValue}'` : transformedValue;
  return `${fieldName} eq ${formattedValue}`;
};

export const getFiltersGlobalFilters = (globalFilters, moduleNamespace = null) => {
  if (_.isNil(globalFilters)) return null;

  // Import the filter configurations registry
  const { FilterConfigRegistry } = require('../input/filters');

  // Extract filters for specific namespace if globalFilters is organized by namespace
  let filters = globalFilters;
  if (moduleNamespace && typeof globalFilters === 'object' && !Array.isArray(globalFilters)) {
    // Check if globalFilters has the namespace key
    if (globalFilters[moduleNamespace]) {
      filters = globalFilters[moduleNamespace];
    }
  }

  const formatClause = (key, value) => {
    if (_.isNil(value)) return null;

    // Extract value from labelInValue objects (from remote selects)
    let actualValue = value;
    if (_.isObject(value) && !_.isArray(value) && 'value' in value && 'label' in value) {
      actualValue = value.value;
    }

    // Handle date range filters using the mapping
    if (dateFilterMap[key]) {
      return generateDateRangeFilter(actualValue, dateFilterMap[key]);
    }

    // Check if value is a special enum object from useOData processing
    if (actualValue?.isEnum && actualValue?.enumNamespace && actualValue?.enumKey) {
      return `${key} eq ${actualValue.enumNamespace}'${actualValue.enumKey}'`;
    }

    const filterConfig = resolveRegisteredFilterConfig(FilterConfigRegistry, moduleNamespace, key);

    // Handle date range filters with custom names (for datepicker type with array values)
    if (
      filterConfig?.type === 'datepicker' &&
      Array.isArray(actualValue) &&
      actualValue.length === 2
    ) {
      return generateDateRangeFilter(actualValue, key);
    }

    // Multi-select: one `eq` per value, grouped with `or`.
    if (Array.isArray(actualValue)) {
      const parts = actualValue.map((item) => formatClause(key, item)).filter(Boolean);
      if (!parts.length) return null;
      if (parts.length === 1) return parts[0];
      return `(${parts.join(' or ')})`;
    }

    return formatRegisteredFilterClause(key, actualValue, filterConfig);
  };

  return _.map(filters, (value, key) => formatClause(key, value))
    .filter(Boolean)
    .join(' and ');
};

export function buildListExportParams({
  globalFilters,
  modelName,
  searchCriteria,
  orderBy,
  defaultOrderBy,
  notSelectSearch,
  expand,
  select,
}) {
  const filters = getFiltersGlobalFilters(globalFilters, modelName);
  return {
    ...(searchCriteria && { search: searchCriteria }),
    ...(orderBy !== null && { orderBy: orderBy ? `${orderBy}` : defaultOrderBy }),
    ...(filters && { filter: filters }),
    ...(notSelectSearch && { notSelectSearch }),
    ...(expand && { expand }),
    ...(select && { select }),
  };
}

//TODO
/**
 * Helper to automatically generate namespace using enum object
 * @param {Object} enumObj - The enum object
 * @param {String} additionalNamespace - Optional additional namespace
 * @returns {String} Full namespace for OData enum
 */
export const getEnumNamespace = (enumObj, additionalNamespace = null) => {
  // Find the enum name in CoreEnums
  const enumName = Object.keys(CoreEnums).find((key) => CoreEnums[key] === enumObj);

  // Build the full namespace
  const baseNamespace = env.ODATA_ENUM_NAMESPACE;

  if (additionalNamespace) {
    return `${baseNamespace}.${additionalNamespace}.${enumName}`;
  }

  return `${baseNamespace}.${enumName}`;
};

/**
 * Gets the HTTP status code text description using
 * @param {string|number} statusCode - The HTTP status code
 * @returns {string} The status code text description
 */
export const getHttpStatusCodeText = (statusCode) => {
  if (!statusCode) return '';

  const code = parseInt(statusCode, 10);

  const enumKey = Object.keys(CoreEnums.ResponseHttpStatusCode).find(
    (key) => CoreEnums.ResponseHttpStatusCode[key] === code,
  );

  if (!enumKey) {
    return `HTTP ${code}`;
  }

  try {
    const { getIntl } = require('umi');
    const { formatMessage } = getIntl();
    return formatMessage({ id: `global.httpResponseStatusCode.${code}` });
  } catch (error) {
    return enumKey;
  }
};

/**
 * Gets the color for HTTP status codes
 * @param {string|number} statusCode - The HTTP status code
 * @returns {string} The color (success, warning, error, default)
 */
export const getHttpStatusCodeColor = (statusCode) => {
  if (!statusCode) return 'default';

  const code = parseInt(statusCode, 10);

  if (code >= 200 && code < 300) return 'success'; // 2xx - Success
  if (code >= 300 && code < 400) return 'warning'; // 3xx - Redirection
  if (code >= 400) return 'error'; // 4xx+ - Client/Server Error
  return 'default'; // Other codes
};

/**
 * Gets the text for HTTP method
 * @param {string|number} method - The HTTP method
 * @returns {string} The method text (GET, POST, PUT, PATCH, DELETE, UNKNOWN)
 */
export const getHttpMethodText = (method) => {
  if (!method) return 'UNKNOWN';

  const code = parseInt(method, 10);
  const enumKey = Object.keys(CoreEnums.HttpMethod).find(
    (key) => CoreEnums.HttpMethod[key] === code,
  );
  return enumKey || 'UNKNOWN';
};

/**
 * Formats file size in bytes to human readable format
 * @param {number} bytes - The file size in bytes
 * @returns {string} Formatted file size (e.g., "1.2 KB", "3.4 MB")
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';
  const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round((bytes / Math.pow(1024, i)) * 100) / 100} ${sizes[i]}`;
};

/**
 * Display label for a chat thread: "number | name" when both exist.
 * @param {object|string|null|undefined} thread
 * @returns {string|null}
 */
export function formatChatThreadLabel(thread) {
  if (thread == null) return null;
  if (typeof thread !== 'object') return thread;
  const number = thread.number;
  const name = thread.name;
  if (number != null && number !== '' && name) return `${number} | ${name}`;
  return name || (number != null && number !== '' ? String(number) : null);
}

/**
 * Build predefined public URLs from options
 * @param {number|string} type - Form field type
 * @param {object} options - Specific id values to build the URL
 */
export const generatePublicFileURL = (type, options = {}) => {
  let publicURL = '';

  const { entityId, fieldId, dataId } = _.pickBy(options, (value) => Regexp.isUUID(value));
  const { postfix, file, fileName } = options || {};

  const search = [file && `file=${file}`, fileName && `fileName=${fileName}`]
    .filter(Boolean)
    .join('&');

  switch (type) {
    case CoreEnums.FormFieldStringType.Image:
    case CoreEnums.FormFieldStringType.Photo:
    case CoreEnums.FormFieldStringType.Signature:

    case CoreEnums.FormFieldType.Image:
    case CoreEnums.FormFieldType.Photo:
    case CoreEnums.FormFieldType.Signature:
      // https://assets.automy.global/entity/{entityId}/show-entity-image/{fieldId}/{dataId}
      publicURL = `entity/${entityId}/show-entity-image/${fieldId}/${dataId}`;
      break;

    case CoreEnums.FormFieldStringType.Attachment:
    case CoreEnums.FormFieldType.Attachment:
      // https://assets.automy.global/entity/{entityId}/download-entity-attachment/{fieldId}/{dataId}
      publicURL = `entity/${entityId}/download-entity-attachment/${fieldId}/${dataId}`;
      break;

    case 'documents':
    case 'generated-documents':
    case 'media':
    case 'email-notification-template':
    case 'bulk-request-tasks':
      // assets...../{type}/{id}/view | {type}/{id}/download
      // e.g. documents, generated-documents, media, email-notification-template, bulk-request-tasks
      publicURL = `${type}/${dataId}/${postfix}`;
      break;

    case 'ai-vector-file':
      // assets...../ai/vector/file/{fileId}/view | .../download
      publicURL = `ai/vector/file/${dataId}/${postfix}`;
      break;

    default:
      break;
  }

  publicURL += search ? `?${search}` : '';
  return { generatedPublicFileURL: `${env.ASSETS_URL}/${publicURL}` };
};

/**
 * Clipboard copy halper
 * @param {string} content - Content to be copied
 */
export const copyToClipboard = async (content = '') => {
  try {
    await navigator.clipboard.writeText(content);
    message.success(getIntl().formatMessage({ id: 'global.clipboard.copied' }));
  } catch (e) {
    // Fallback for browsers that don't support clipboard API
    const textarea = document.createElement('textarea');
    textarea.value = content;

    document.body.appendChild(textarea);

    textarea.focus();
    textarea.select();

    try {
      document.execCommand('copy');
      message.success(getIntl().formatMessage({ id: 'global.clipboard.copied' }));
    } catch (fe) {
      message.error(getIntl().formatMessage({ id: 'global.clipboard.error' }));
    }

    document.body.removeChild(textarea);
  }
};
